import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { motion } from 'framer-motion';

function AdminLogin({ setAdminLoggedIn }) {
  const [step, setStep] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const navigate = useNavigate();

  const loginUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_REQUESTING_API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const res = await response.json();

      if (response.status === 201) {
        localStorage.setItem("usertoken", res.result.token);
        setAdminLoggedIn(true);
        toast.success("Login successful!");
        navigate("/admin/dashboard");
      } else {
        toast.error(res.error || "Invalid email or password");
      }
    } catch (err) {
      toast.error("Server error while logging in");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      return toast.error("Please enter your email first");
    }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_REQUESTING_API}/api/admin/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (res.ok) {
        toast.success("OTP sent to your email");
        setStep('reset');
      } else {
        toast.error(data?.error || "Failed to send OTP");
      }
    } catch (err) {
      toast.error("Error sending OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_REQUESTING_API}/api/admin/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp, newPassword }),
      });

      const data = await res.json();

      if (res.ok) {
        toast.success("Password updated! Please login.");
        setStep('login');
        setOtp('');
        setNewPassword('');
        setPassword('');
      } else {
        toast.error(data?.error || "Invalid OTP or failed to reset password");
      }
    } catch (err) {
      toast.error("Server error during password reset");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black p-4">
      <ToastContainer position="top-center" />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-96 h-96 bg-blue-500/10 rounded-full blur-3xl -top-48 -left-48 animate-pulse"></div>
        <div className="absolute w-96 h-96 bg-purple-500/10 rounded-full blur-3xl -bottom-48 -right-48 animate-pulse delay-1000"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Logo/Header Section */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-20 h-20 bg-gradient-to-br from-gray-700 to-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-2xl"
          >
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </motion.div>
          <h1 className="text-3xl font-black text-white mb-2">
            {step === 'login' ? 'Admin Portal' : step === 'forgot' ? 'Password Recovery' : 'Reset Password'}
          </h1>
          <p className="text-gray-400">
            {step === 'login' ? 'Enter your credentials to continue' : step === 'forgot' ? 'We\'ll send you an OTP' : 'Enter OTP and new password'}
          </p>
        </div>

        {/* Form Card */}
        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white/10 backdrop-blur-xl p-8 rounded-2xl shadow-2xl border border-white/20"
          onSubmit={
            step === 'login'
              ? loginUser
              : step === 'forgot'
              ? handleForgotPassword
              : handleResetPassword
          }
        >
          <div className="space-y-5">
            {/* Email Input */}
            <div>
              <label className="block text-sm font-semibold text-white mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                </div>
                <input
                  type="email"
                  placeholder="admin@example.com"
                  className="w-full pl-12 pr-4 py-3 bg-white/10 border-2 border-white/20 rounded-xl text-white placeholder-gray-400 focus:border-white/40 focus:outline-none transition-all"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={step === 'reset'}
                  required
                />
              </div>
            </div>

            {/* Password Input (Login) */}
            {step === 'login' && (
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    className="w-full pl-12 pr-12 py-3 bg-white/10 border-2 border-white/20 rounded-xl text-white placeholder-gray-400 focus:border-white/40 focus:outline-none transition-all"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition-colors"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* OTP and New Password (Reset) */}
            {step === 'reset' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    OTP Code
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      className="w-full pl-12 pr-4 py-3 bg-white/10 border-2 border-white/20 rounded-xl text-white placeholder-gray-400 focus:border-white/40 focus:outline-none transition-all"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <input
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      className="w-full pl-12 pr-12 py-3 bg-white/10 border-2 border-white/20 rounded-xl text-white placeholder-gray-400 focus:border-white/40 focus:outline-none transition-all"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition-colors"
                    >
                      {showNewPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-gray-900 py-3.5 rounded-xl font-bold text-base hover:bg-gray-100 disabled:bg-gray-500 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 mt-6"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-3">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </span>
              ) : (
                step === 'login' ? 'Sign In' : step === 'forgot' ? 'Send OTP' : 'Reset Password'
              )}
            </button>

            {/* Footer Links */}
            <div className="text-center mt-6">
              {step === 'login' && (
                <button
                  type="button"
                  onClick={() => setStep('forgot')}
                  className="text-sm text-gray-300 hover:text-white transition-colors font-medium"
                >
                  Forgot your password?
                </button>
              )}
              {step !== 'login' && (
                <button
                  type="button"
                  onClick={() => {
                    setStep('login');
                    setOtp('');
                    setNewPassword('');
                  }}
                  className="text-sm text-gray-300 hover:text-white transition-colors font-medium flex items-center justify-center gap-2 mx-auto"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Login
                </button>
              )}
            </div>
          </div>
        </motion.form>

        {/* Footer Text */}
        <p className="text-center text-gray-500 text-sm mt-8">
          © 2024 Admin Portal. All rights reserved.
        </p>
      </motion.div>
    </div>
  );
}

export default AdminLogin;


// working 16/12/25

// import React, { useState } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { toast } from 'react-toastify';
// import { ToastContainer } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css'; 

// function AdminLogin({ setAdminLoggedIn }) {
//   const [step, setStep] = useState('login'); // login | forgot | reset
//   const [email, setEmail] = useState('');
//   const [password, setPassword] = useState('');
//   const [otp, setOtp] = useState('');
//   const [newPassword, setNewPassword] = useState('');
//   const navigate = useNavigate();

//   const loginUser = async (e) => {
//   e.preventDefault();
//   try {
//     const response = await fetch(`${process.env.REACT_APP_REQUESTING_API}/api/admin/login`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({ email, password }),
//     });

//     const res = await response.json();

//     if (response.status === 201) {
//       localStorage.setItem("usertoken", res.result.token);
//       setAdminLoggedIn(true);
//       navigate("/admin/dashboard");
//     } else {
//       toast.error(res.error || "Invalid email or password");
//     }
//   } catch (err) {
//     toast.error("Server error while logging in");
//   }
// };


//   const handleForgotPassword = async (e) => {
//     e.preventDefault();
//     if (!email) {
//       return toast.error("Please enter your email first");
//     }
//     try {
//       const res = await fetch(`${process.env.REACT_APP_REQUESTING_API}/api/admin/forgot-password`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ email }),
//       });

//       const data = await res.json();

//       if (res.ok) {
//         toast.success("OTP sent to your email");
//         setStep('reset');
//       } else {
//         toast.error(data?.error || "Failed to send OTP");
//       }
//     } catch (err) {
//       toast.error("Error sending OTP");
//     }
//   };

//   const handleResetPassword = async (e) => {
//     e.preventDefault();
//     try {
//       const res = await fetch(`${process.env.REACT_APP_REQUESTING_API}/api/admin/reset-password`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ email, otp, newPassword }),
//       });

//       const data = await res.json();

//       if (res.ok) {
//         toast.success("Password updated! Please login.");
//         setStep('login');
//         setOtp('');
//         setNewPassword('');
//         setPassword('');
//       } else {
//         toast.error(data?.error || "Invalid OTP or failed to reset password");
//       }
//     } catch (err) {
//       toast.error("Server error during password reset");
//     }
//   };

//   return (
//     <div className="min-h-screen flex items-center justify-center bg-black text-black">
//        <ToastContainer position="top-center" />
//       <form
//         className="bg-white p-10 rounded shadow-md w-[400px]"
//         onSubmit={
//           step === 'login'
//             ? loginUser
//             : step === 'forgot'
//             ? handleForgotPassword
//             : handleResetPassword
//         }
//       >
//         <h2 className="text-2xl mb-6 text-center text-black font-bold">
//           {step === 'login' ? 'Admin Login' : step === 'forgot' ? 'Forgot Password' : 'Reset Password'}
//         </h2>

//         {/* Email input */}
//         <input
//           type="email"
//           placeholder="Email"
//           className="w-full mb-4 p-2 border"
//           value={email}
//           onChange={(e) => setEmail(e.target.value)}
//           disabled={step === 'reset'} // lock email input in reset step
//         />

//         {step === 'login' && (
//           <input
//             type="password"
//             placeholder="Password"
//             className="w-full mb-4 p-2 border"
//             value={password}
//             onChange={(e) => setPassword(e.target.value)}
//           />
//         )}

//         {step === 'reset' && (
//           <>
//             <input
//               type="text"
//               placeholder="Enter OTP"
//               className="w-full mb-4 p-2 border"
//               value={otp}
//               onChange={(e) => setOtp(e.target.value)}
//             />
//             <input
//               type="password"
//               placeholder="New Password"
//               className="w-full mb-4 p-2 border"
//               value={newPassword}
//               onChange={(e) => setNewPassword(e.target.value)}
//             />
//           </>
//         )}

//         <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700">
//           {step === 'login'
//             ? 'Login'
//             : step === 'forgot'
//             ? 'Send OTP'
//             : 'Reset Password'}
//         </button>

//         {/* Step toggles */}
//        {step === 'login' && (
//   <p
//     className="text-sm mt-4 text-blue-700 cursor-pointer text-center"
//     onClick={() => {
//       setStep('forgot');
//     }}
//   >
//     Forgot Password?
//   </p>
// )}
//         {step !== 'login' && (
//           <p
//             className="text-sm mt-4 text-blue-700 cursor-pointer text-center"
//             onClick={() => {
//               setStep('login');
//               setOtp('');
//               setNewPassword('');
//             }}
//           >
//             Back to Login
//           </p>
//         )}
//       </form>
//     </div>
//   );
// }

// export default AdminLogin;

